import type { Schemas } from '@npc/api-client'

import { cn } from '../../components/cn'
import { hhmm } from '../../app/format'
import { T } from '../../copy/text'

/**
 * 手机 mockup。**它不是一个告警列表，是「一条通知长什么样」的样例。**
 *
 * 设计稿把它放在这一屏的右边，和左边那两块（什么时候才吵你 / 最近一次真出事）
 * 一起回答同一个问题：这个产品什么时候会打扰你、打扰你的时候说什么。
 * 所以它只用最近的一条真告警，不翻页也不列全部。
 *
 * 340px 宽、40px 圆角、外面那圈是 `#14150F`——都是设计稿的原值。
 */
export function Phone({ alert, now }: { alert: Schemas['Alert']; now: string }) {
  return (
    <div className="flex flex-col items-center gap-npc-gap-lg">
      <div className="w-[var(--container-npc-phone-mock)] rounded-npc-phone bg-npc-dark p-npc-gap shadow-npc-phone">
        {/* 手机内屏 30px。外壳那圈 40px 有 token（phone-mock-radius），内屏这一档没有。
            提给流 0 了，补上就把这行换成 rounded-npc-phone-inner */}
        <div className="overflow-hidden rounded-[30px] bg-npc-bg"> {/* npc-token-allow: 手机内屏圆角，token 里缺这一档，已提给流 0 */}
          <div className="flex justify-between px-npc-grid-lg pb-npc-gap-sm pt-npc-gap-lg font-npc-display text-npc-caption-sm font-medium">
            <span>{hhmm(now)}</span>
            <span>NPC 小队</span>
          </div>

          <div className="px-npc-card-sm pb-npc-grid-lg pt-npc-gap-xs">
            <Notification alert={alert} />

            <div className="mt-[10px] rounded-npc-card-lg border border-npc-border bg-npc-panel p-[15px]">
              <div className="mb-[10px] text-npc-body-sm font-semibold">
                <T id="alert.card.ask" />
              </div>
              <div className="flex flex-col gap-[8px]">
                <button
                  type="button"
                  className="w-full cursor-pointer rounded-npc-button border-none bg-npc-dark p-npc-gap font-[inherit] text-npc-body-sm text-npc-dark-ink"
                >
                  <T id="alert.card.how_fixed" />
                </button>
                <button
                  type="button"
                  className="w-full cursor-pointer rounded-npc-button border border-npc-border bg-npc-panel p-npc-gap font-[inherit] text-npc-body-sm"
                >
                  {/* 「以后这种别叫醒我」——**不是「屏蔽」也不是「静音」**。
                      设计稿的说法带着「叫醒」这个动作，用户才知道自己关掉的是什么 */}
                  <T id="alert.card.mute_this_kind" />
                </button>
              </div>
            </div>

            {/* 设计稿这儿还有一句「其余 19 台机器一切正常」。
                **没做**：契约里没有哪个字段给得出这个数。
                自己拿机器总数减去有告警的那些，等于前端定义了「什么算正常」——
                那是后端的判断。缺口写在交付报告里 */}
          </div>
        </div>
      </div>

      <div className="text-npc-caption-sm text-npc-ink-softer">
        <T id="alert.phone.hint" />
      </div>
    </div>
  )
}

/**
 * 那张通知卡。
 *
 * 底色按 `severity` 走，**顶上那行「刚刚 · 已经处理好了」只在真处理好了时出现**
 * （`state === 'resolved'`）——还开着的告警上写这一句就是假话，
 * 而别的状态 `docs/copy` 里没有对应的句子，所以那一行整个不显示。
 */
function Notification({ alert }: { alert: Schemas['Alert'] }) {
  const skin = {
    critical: 'bg-npc-red-bg border-npc-red-border',
    warn: 'bg-npc-amber-bg border-npc-amber-border',
    info: 'bg-npc-blue-bg border-npc-blue',
  }[alert.severity]

  const ink = {
    critical: 'text-npc-red-ink',
    warn: 'text-npc-amber-ink',
    info: 'text-npc-blue-ink',
  }[alert.severity]

  return (
    <div className={cn('rounded-npc-card-lg border p-[15px]', skin)}>
      {alert.state === 'resolved' ? (
        <div className={cn('mb-[7px] text-npc-meta', ink)}>
          <T id="alert.card.handled" />
        </div>
      ) : null}
      <div className="mb-[8px] text-npc-body-xl font-semibold leading-[1.45]">{alert.title}</div>
      {/* 「每条告警都要说清影响谁」——这一句是必填的，后端说不清时
          用 alert.affected_unknown 那条，也不会是空的 */}
      <div className={cn('text-npc-body-sm leading-[1.6]', ink)}>{alert.affected}</div>
    </div>
  )
}
